import { isBadCoverUrl } from '../utils/coverArt';
import { qobuzStaticCoverUrl } from '../utils/qobuzStaticCoverUrl';
import { streamingService } from '../services/streamingService';
import { searchService } from '../services/searchService';

/** Ella reason string from generate/continue API track objects. */
function ellaSelectionReason(tr: any): string | undefined {
  const raw = tr.selectionReason ?? tr.selection_reason ?? tr.reason ?? tr.arcRole ?? tr.arc_role;
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed || undefined;
}

function pickNonEmptyCover(...candidates: unknown[]): string {
  for (const raw of candidates) {
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (trimmed && !isBadCoverUrl(trimmed)) return trimmed;
  }
  return '';
}

function pickAlbumId(tr: any): string {
  return String(tr.album_id || tr.albumId || tr.serviceAlbumId || tr.album?.id || '').trim();
}

/** Extract cover URL from Qobuz album-detail response (same field order as AlbumCoverCard). */
export function extractQobuzAlbumCoverFromDetail(data: any): string | null {
  if (!data) return null;
  const url = pickNonEmptyCover(
    data.image?.large,
    data.image?.small,
    data.image?.thumbnail,
    typeof data.image === 'string' ? data.image : '',
    data.cover,
    data.coverUrl,
    data.cover_url,
    data.coverUrlLarge,
    data.cover_url_large,
    data.artworkUrl,
    data.cover_art_url,
  );
  return url || null;
}

const qobuzAlbumCoverInflight = new Map<string, Promise<string | null>>();

function stripQobuzServicePrefix(id: string): string {
  return String(id || '').replace(/^qobuz:(album|track|artist):/, '').trim();
}

/** Resolve Qobuz album art via GET /api/streaming/qobuz/album-detail (album view path). */
export async function resolveQobuzAlbumCoverUrl(albumId: string | null | undefined): Promise<string | null> {
  const key = stripQobuzServicePrefix(String(albumId || ''));
  if (!key) return null;
  if (!qobuzAlbumCoverInflight.has(key)) {
    qobuzAlbumCoverInflight.set(
      key,
      streamingService
        .getQobuzAlbumDetail(key)
        .then(extractQobuzAlbumCoverFromDetail)
        .catch(() => null),
    );
  }
  return qobuzAlbumCoverInflight.get(key)!;
}

export type EllaApiTrack = ReturnType<typeof ellaDetailTrackToApiTrack>;

/**
 * Normalize GET /api/playlists/smart/:id track rows to generate/continue shape.
 *
 * Mismatch vs legacy generate/continue (camelCase, cover always populated from Qobuz):
 * - Detail rows use snake_case: track_id, cover_url, album_id, artist_name, album_title,
 *   duration_seconds, selection_reason.
 * - Stage-2 poll rows often have cover_url="" and album_id="" when the worker reused a
 *   pool qobuzTrackId without persisting album metadata — mapping alone cannot invent art.
 */
export function ellaDetailTrackToApiTrack(tr: any) {
  return {
    trackId: String(tr.track_id || tr.trackId || tr.id || ''),
    source: tr.source || 'qobuz',
    title: tr.title || 'Unknown',
    artistName: tr.artist_name || tr.artistName || tr.artist || 'Unknown',
    albumTitle: tr.album_title || tr.albumTitle || tr.album || '',
    albumId: pickAlbumId(tr),
    coverUrl: pickNonEmptyCover(
      tr.cover_url,
      tr.coverUrl,
      tr.artworkUrl,
      tr.artwork_url,
      tr.image?.large,
      tr.image?.small,
      tr.album?.image?.large,
      tr.album?.image?.small,
      qobuzStaticCoverUrl(pickAlbumId(tr)),
    ),
    duration: tr.duration_seconds ?? tr.duration ?? 0,
    position: tr.position ?? 0,
    selectionReason: tr.selection_reason || tr.selectionReason || tr.arcRole || tr.arc_role || '',
    energyEstimate: tr.energy_estimate ?? tr.energyEstimate,
    arcRole: tr.arcRole || tr.arc_role || tr.segment_id,
  };
}

/** Fill missing coverUrl on poll/appended tracks — album-detail first, unified search fallback. */
export async function enrichEllaApiTracksWithCoverArt<T extends EllaApiTrack>(tracks: T[]): Promise<T[]> {
  if (!tracks.length) return tracks;
  return Promise.all(
    tracks.map(async (tr) => {
      if (tr.coverUrl && !isBadCoverUrl(tr.coverUrl)) return tr;

      let coverUrl = tr.coverUrl || '';
      if (tr.albumId) {
        const fromAlbum = await resolveQobuzAlbumCoverUrl(tr.albumId);
        if (fromAlbum) coverUrl = fromAlbum;
      }

      if (!coverUrl && tr.trackId && tr.artistName && tr.title) {
        try {
          const res = await searchService.unified(`${tr.artistName} ${tr.title}`, 8);
          const match = res.results?.tracks?.find(
            (t) => String(t.serviceTrackId || t.id) === String(tr.trackId),
          );
          if (match?.coverUrl && !isBadCoverUrl(match.coverUrl)) {
            coverUrl = match.coverUrl;
          }
        } catch {
          /* search optional */
        }
      }

      if (!coverUrl || coverUrl === tr.coverUrl) return tr;
      return { ...tr, coverUrl };
    }),
  );
}

/** Session track shape used by MaestroRadioPage now-playing / queue panel. */
export function ellaApiTrackToSessionTrack(tr: any) {
  const reason = ellaSelectionReason(tr);
  const coverUrl = tr.coverUrl || tr.artworkUrl || '';
  return {
    id: tr.trackId,
    title: tr.title,
    artist: tr.artistName,
    album: tr.albumTitle,
    albumId: tr.albumId,
    coverUrl,
    artworkUrl: coverUrl,
    duration: tr.duration,
    source: tr.source,
    ...(reason ? { reason } : {}),
  };
}

/** Convert smart playlist / continue API tracks to flat queue track objects. */
export function ellaTracksToQueueFormat(tracks: any[]) {
  return tracks.map((tr) => {
    const dur = (tr.duration || 0) > 10000 ? Math.floor(tr.duration / 1000) : (tr.duration || 0);
    const trackId = tr.trackId || tr.id;
    const reason = ellaSelectionReason(tr);
    const cover = tr.coverUrl || tr.artworkUrl || '';
    return {
      id: trackId,
      qobuzTrackId: trackId,
      title: tr.title || 'Unknown',
      artist: tr.artistName || tr.artist || 'Unknown',
      album: tr.albumTitle || tr.album || '',
      albumId: tr.albumId || '',
      artworkUrl: cover,
      coverUrl: cover,
      duration: dur,
      source: tr.source || 'qobuz',
      ...(reason ? { reason } : {}),
    };
  });
}
